import React from "react";
import { Avatar, Box, Card, Grid, Typography } from "@mui/material";
import { Fingerprint } from "@mui/icons-material";
import styles from "./cisoDashboard.module.scss";
import ComplianceAuditPanel from "./ComplianceAuditPanel";
import ImmutableAuditLog from "./ImmutableAuditLog";
import PolicyDeviationTrend from "./PolicyDeviationTrend";
import OTProtocolAnomaliesCard from "./OTProtocolAnomaliesCard";
import MicroSegmentationHealth from "./MicroSegmentationHealth";
import PreventionSuccess from "./PreventionSuccess";
import CriticalAssetsTable from "./CriticalAssetsTable";
import MitreTechniquesCard from "./MitreTechniquesCard";
import AnomalyDetectionConfidence from "./AnomalyDetectionConfidence";
import AssetRiskHeatmap from "./AssetRiskHeatmap";
import ThreatExposureAnalysis from "./ThreatExposureAnalysis";
import ContinuousAuthenticationMonitor from "./ContinuousAuthenticationMonitor";
import SpeedIcon from "@mui/icons-material/Speed";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { ElectricBoltOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
const statCards = [
  {
    title: "Risk Score",
    value: "42",
    delta: "+8% vs 30d",
    deltaTone: "positive",
    pill: "Risk",
    stroke: "#0CA6F2",
    icon: SpeedIcon,
  },
  {
    title: "Zero Trust Health",
    value: "96.8%",
    delta: "verified",
    deltaTone: "neutral",
    pill: "Trust",
    stroke: "#00BFA6",
    icon: VerifiedUserOutlinedIcon,
  },
  {
    title: "Incident Velocity",
    value: "127",
    delta: "12% vs 30d",
    deltaTone: "positive",
    pill: "Velocity",
    stroke: "#F59E0B",
    icon: ElectricBoltOutlined,
  },
  {
    title: "Trust Provenance (DAG)",
    value: "99.9%",
    delta: "Chain-of-Custody",
    deltaTone: "neutral",
    pill: "Chain",
    stroke: "#00B0DF",
    icon: Fingerprint,
  },
];

const TrendPill = ({ label, icon: Icon, bg, color }) => {
  return (
    <Avatar
      variant="rounded"
      sx={{
        width: 40,
        height: 40,
        bgcolor: bg,
        color: color,
        borderRadius: "10px",
      }}
    >
      <Icon fontSize="small" />
    </Avatar>
  );
};

const deltaClass = (tone) => {
  if (tone === "positive") return styles.deltaPositive;
  if (tone === "negative") return styles.deltaNegative;
  return styles.deltaNeutral;
};
export const CisoExecutiveDashboard = () => {
  const navigate = useNavigate();

  const handleNavigate = (path) => {
    navigate(`cDashboard/${path}`);
  };

  return (
    <Box className={styles.page}>
      <Grid
        container
        spacing={2}
        className={styles.statGrid}
        alignItems="stretch"
      >
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={card.title} display="flex">
            <Card
              className={styles.card}
              variant="outlined"
              elevation={0}
              sx={{ borderRadius: 2, width: "100%", cursor: "pointer" }}
              onClick={() =>
                handleNavigate(
                  index == 0
                    ? "risk-score"
                    : index == 1
                    ? "zero-trust"
                    : "block-chain"
                )
              }
            >
              <Box className={styles.cardTitle}>
                <TrendPill
                  label={card.pill}
                  icon={card.icon}
                  bg={card.stroke}
                  color={card.color}
                />
                <span>{card.title}</span>
              </Box>
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    className={styles.value}
                    fontSize={40}
                    fontWeight={700}
                  >
                    {card.value}
                  </Typography>
                  <svg width="64" height="28" viewBox="0 0 64 28">
                    <polyline
                      fill="none"
                      stroke={card.stroke}
                      strokeWidth="3"
                      strokeLinecap="round"
                      points="0,20 12,16 24,22 36,10 48,14 60,6"
                    />
                  </svg>
                </Box>
                <Typography
                  variant="body2"
                  fontSize={16}
                  className={deltaClass(card.deltaTone)}
                >
                  {card.delta}
                </Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12}>
          <ContinuousAuthenticationMonitor />
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <MitreTechniquesCard handleNavigate={handleNavigate} />
        </Grid>
        <Grid item xs={12} md={6}>
          <AnomalyDetectionConfidence />
        </Grid>
        <Grid item xs={12} md={6} display="flex">
          <AssetRiskHeatmap />
        </Grid>
        <Grid item xs={12} md={6} display="flex">
          <ThreatExposureAnalysis handleNavigate={handleNavigate} />
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={8} display="flex">
          <ComplianceAuditPanel handleNavigate={handleNavigate} />
        </Grid>
        <Grid item xs={12} md={4} display="flex">
          <ImmutableAuditLog />
        </Grid>
        <Grid item xs={12}>
          <PolicyDeviationTrend />
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={4} display="flex">
          <OTProtocolAnomaliesCard />
        </Grid>
        <Grid item xs={12} md={4} display="flex">
          <MicroSegmentationHealth />
        </Grid>
        <Grid item xs={12} md={4} display="flex">
          <PreventionSuccess />
        </Grid>
        <Grid item xs={12} mb={3}>
          <CriticalAssetsTable handleNavigate={handleNavigate} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CisoExecutiveDashboard;
